/**
 * Three.js "fish-tank" scene for visualizing GPS data.
 *
 * The scene is a unit cube of side `CUBE_SIZE` centered horizontally at the
 * origin and resting on y=0:
 *
 *   - bottom face (y=0): textured with a Google Static Maps image
 *   - vertical Y axis: time (windowStart at y=0, windowEnd at y=CUBE_HEIGHT)
 *   - +X = east, -Z = north (matches map texture orientation)
 *
 * Trackpoints are rendered as small dots connected in timestamp order.
 * Visit records are
 * rendered as instanced cylinders that span (arrival → departure) along Y at
 * the visit's lat/lng, with a configurable radius.
 *
 * Camera control is `OrbitControls` so the user can drag to rotate the cube
 * around any axis ("tilt the fish tank").
 */
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

import { type LatLngBounds, buildStaticMapUrl, fitStaticMap, imageBoundsAt, projectToUnit } from './static-map.js'
import {
  type SourceFilters,
  type TrackPoint,
  type VisitRecord,
  SOURCE_FALLBACK_LOCATION,
  SOURCE_LIVE_UPDATES,
  SOURCE_RGB,
  SOURCE_SLC,
} from './types.js'
import { toEpoch } from './helpers.js'

/** Horizontal extent of the cube in Three.js units. */
export const CUBE_SIZE = 10
/** Vertical (time) extent of the cube. Same as CUBE_SIZE for a true cube look. */
export const CUBE_HEIGHT = 10

/** Named camera presets exposed via FishTankScene.setView(). */
export type ViewPreset = 'default' | 'top' | 'front' | 'side' | 'bottom'

/**
 * Rendering mode.
 *  - '3d': Y axis is time. Visit cylinders span (arrival → departure) along Y,
 *    trackpoints float at Y = altitudeOf(timestamp), the cube wireframe is shown.
 *  - '2d': Y axis represents *visit duration only*. Visit bars stand on the map
 *    plane (y=0) with height proportional to duration. Trackpoints lie flat on
 *    the map plane connected by lines. The cube wireframe is hidden.
 */
export type ViewMode = '2d' | '3d'

// ── pure helpers (defined above the class so methods can call them) ──

/** Compute u/v in [-0.5, 0.5] using the static map's bounds. */
function uv(lat: number, lng: number, bounds: LatLngBounds): { u: number; vCoord: number } {
  const { u, v } = projectToUnit(lat, lng, bounds)
  return { u, vCoord: v }
}

/** Visit cylinder radius in cube units. Made small enough to not occlude the
 * trackpoint geometry inside the cube. */
function visitRadius(horizontalAccuracy: number): number {
  const a = Math.max(10, Math.min(120, horizontalAccuracy || 30))
  // Map ~10–120m to ~0.05–0.18 cube units.
  return 0.04 + ((a - 10) / 110) * 0.14
}

/**
 * 2D-mode bar height for a visit, in cube units.
 * Linear scale: 1 hour ≈ 0.4 units, capped at ~80% of cube height. So:
 *   30min  → 0.2u   (small but visible)
 *   2 h    → 0.8u
 *   8 h    → 3.2u
 *   20 h+  → 8u (capped)
 */
function visitBarHeight2D(durationMs: number): number {
  const HOUR_MS = 3_600_000
  const hours = Math.max(0.1, durationMs / HOUR_MS)
  const raw = hours * 0.4
  return Math.min(CUBE_HEIGHT * 0.8, raw)
}

/** Y offset for 2D-mode trackpoint geometry. Just enough above the textured
 *  bottom plane to avoid z-fighting. */
const TRACKPOINT_2D_Y = 0.05

const VISIT_COLOR_COMPLETE = new THREE.Color(0.0, 0.48, 1.0) // #007AFF
const VISIT_COLOR_ONGOING = new THREE.Color(1.0, 0.58, 0.0) // #FF9500

type TrackPointGroup = { positions: number[] }

function getTrackPointGroup(groups: Map<number, TrackPointGroup>, source: number): TrackPointGroup {
  let group = groups.get(source)
  if (!group) {
    group = { positions: [] }
    groups.set(source, group)
  }
  return group
}

export type SceneOptions = {
  container: HTMLElement
  apiKey: string
}

export type SceneData = {
  trackPoints: TrackPoint[] // already filtered to selected window
  visits: VisitRecord[] // already filtered & deduped
  filters: SourceFilters
  windowStart: number
  windowEnd: number
  mode: ViewMode
}

export class FishTankScene {
  private container: HTMLElement
  private apiKey: string

  private scene: THREE.Scene
  private camera: THREE.PerspectiveCamera
  private renderer: THREE.WebGLRenderer
  private controls: OrbitControls
  // eslint-disable-next-line no-undef
  private resizeObserver: ResizeObserver | null = null
  private animationId: number | null = null

  // Long-lived scene objects we mutate when data changes.
  private dataGroup: THREE.Group
  private cubeWireframe: THREE.LineSegments
  private bottomPlane: THREE.Mesh
  private bottomMaterial: THREE.MeshBasicMaterial
  private currentBounds: LatLngBounds | null = null
  private staticMapDebounce: number | null = null

  constructor(opts: SceneOptions) {
    this.container = opts.container
    this.apiKey = opts.apiKey

    // ── renderer ──
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setClearColor(0xf3f3f5, 1)
    this.container.appendChild(this.renderer.domElement)

    // ── scene ──
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0xf3f3f5)

    // ── camera ──
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000)
    // Default view: angled from the front-right, looking down at the cube
    this.camera.position.set(CUBE_SIZE * 1.2, CUBE_HEIGHT * 1.1, CUBE_SIZE * 1.4)
    this.camera.lookAt(0, CUBE_HEIGHT / 2, 0)

    // ── lighting ──
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.7))
    const dir = new THREE.DirectionalLight(0xffffff, 0.6)
    dir.position.set(10, 20, 10)
    this.scene.add(dir)

    // ── orbit controls (drag to tilt the fish tank) ──
    this.controls = new OrbitControls(this.camera, this.renderer.domElement)
    this.controls.target.set(0, CUBE_HEIGHT / 2, 0)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.08
    this.controls.minDistance = CUBE_SIZE * 0.5
    this.controls.maxDistance = CUBE_SIZE * 6
    this.controls.maxPolarAngle = Math.PI * 0.95 // allow looking from below if you want
    this.controls.update()

    // ── cube wireframe ──
    const boxGeo = new THREE.BoxGeometry(CUBE_SIZE, CUBE_HEIGHT, CUBE_SIZE)
    boxGeo.translate(0, CUBE_HEIGHT / 2, 0)
    const edges = new THREE.EdgesGeometry(boxGeo)
    boxGeo.dispose()
    const edgeMat = new THREE.LineBasicMaterial({ color: 0x9aa0a6, transparent: true, opacity: 0.7 })
    this.cubeWireframe = new THREE.LineSegments(edges, edgeMat)
    this.scene.add(this.cubeWireframe)

    // ── bottom plane (placeholder gray, replaced when first map loads) ──
    const planeGeo = new THREE.PlaneGeometry(CUBE_SIZE, CUBE_SIZE)
    planeGeo.rotateX(-Math.PI / 2) // lay flat on the XZ plane
    this.bottomMaterial = new THREE.MeshBasicMaterial({ color: 0xe8eaed, side: THREE.DoubleSide })
    this.bottomPlane = new THREE.Mesh(planeGeo, this.bottomMaterial)
    this.bottomPlane.position.set(0, 0, 0)
    this.scene.add(this.bottomPlane)

    // ── data group (cleared and rebuilt on data updates) ──
    this.dataGroup = new THREE.Group()
    this.scene.add(this.dataGroup)

    // ── observer + animation loop ──
    this.handleResize()
    this.resizeObserver = new window.ResizeObserver(() => this.handleResize())
    this.resizeObserver.observe(this.container)
    this.startLoop()
  }

  // ── public API ──

  /** Replace the rendered data and refresh the bottom map texture if needed. */
  setData(data: SceneData) {
    this.clearDataGroup()

    // Cube wireframe is only meaningful in 3D mode (Y axis = time). In 2D mode
    // the Y axis is just visit duration, so the "ceiling" is meaningless.
    this.cubeWireframe.visible = data.mode === '3d'

    // Bbox is computed from trackpoints ONLY. Visits can have wildly different
    // locations (an "ongoing" visit from another city can survive the time
    // filter and span the bbox across multiple cities, leaving the static map
    // zoomed out to the whole continent).
    const fitLatLngs: { lat: number; lng: number }[] = []
    for (const p of data.trackPoints) {
      if (this.isSourceEnabled(p.source, data.filters)) {
        fitLatLngs.push({ lat: p.latitude, lng: p.longitude })
      }
    }
    // If there are no visible trackpoints, fall back to visit positions so we
    // still have *something* to fit the map to.
    if (fitLatLngs.length === 0 && data.filters.visits) {
      for (const v of data.visits) {
        fitLatLngs.push({ lat: v.latitude, lng: v.longitude })
      }
    }

    if (fitLatLngs.length === 0) {
      return
    }

    this.refitBottomMap(fitLatLngs)

    if (!this.currentBounds) {
      const fit = fitStaticMap(fitLatLngs, this.apiKey)
      if (fit) this.currentBounds = fit.bounds
    }
    if (!this.currentBounds) return

    const span = data.windowEnd - data.windowStart
    if (span <= 0) return

    // ── visit cylinders ──
    if (data.filters.visits && data.visits.length > 0) {
      if (data.mode === '3d') {
        this.addVisits(data.visits, data.windowStart, data.windowEnd, this.currentBounds)
      } else {
        this.addVisits2D(data.visits, data.windowStart, data.windowEnd, this.currentBounds)
      }
    }

    // ── trackpoints (lines + dots) ──
    if (data.mode === '3d') {
      this.addTrackPoints(data.trackPoints, data.filters, data.windowStart, data.windowEnd, this.currentBounds)
    } else {
      this.addTrackPoints2D(data.trackPoints, data.filters, this.currentBounds)
    }
  }

  /**
   * Snap the camera to a named preset view. The OrbitControls target is always
   * the cube center; we just relocate the camera. Damping handles the brief
   * "fly to" animation on the next animation frame.
   */
  setView(preset: ViewPreset) {
    const center = new THREE.Vector3(0, CUBE_HEIGHT / 2, 0)
    let pos: [number, number, number]
    switch (preset) {
      case 'top':
        // Straight down. The 0.001 offset is on Z ONLY (not both X and Z) so
        // the camera's "up" cross-product resolves cleanly to +X (east) and
        // its "screen up" to -Z (north). Offsetting both axes makes the cross
        // product end up on a NE diagonal, which rotates the rendered cube by
        // 45° on screen.
        pos = [0, CUBE_HEIGHT * 2.5, 0.001]
        break
      case 'front':
        // Looking at the cube's south face (toward +Z).
        pos = [0, CUBE_HEIGHT * 0.5, CUBE_SIZE * 2.2]
        break
      case 'side':
        // Looking from the east (+X), seeing the right face.
        pos = [CUBE_SIZE * 2.2, CUBE_HEIGHT * 0.5, 0]
        break
      case 'bottom':
        // 仰视: from below the floor looking up at the cube. Polar angle
        // ≈ 113° here, well within OrbitControls' maxPolarAngle (171°).
        pos = [CUBE_SIZE * 1.4, -CUBE_HEIGHT * 0.5, CUBE_SIZE * 1.4]
        break
      case 'default':
      default:
        // 3/4 oblique from front-right at ~45° elevation.
        pos = [CUBE_SIZE * 1.2, CUBE_HEIGHT * 1.1, CUBE_SIZE * 1.4]
        break
    }
    this.camera.position.set(pos[0], pos[1], pos[2])
    this.controls.target.copy(center)
    this.controls.update()
  }

  /**
   * Rotate the camera around the cube's vertical axis by `deltaRadians`,
   * preserving its current elevation and distance. Use ±π/2 for "rotate 90°".
   */
  rotateAzimuth(deltaRadians: number) {
    const target = this.controls.target
    const offset = new THREE.Vector3().subVectors(this.camera.position, target)
    const spherical = new THREE.Spherical().setFromVector3(offset)
    spherical.theta += deltaRadians
    offset.setFromSpherical(spherical)
    this.camera.position.copy(target).add(offset)
    this.controls.update()
  }

  dispose() {
    if (this.animationId !== null) window.cancelAnimationFrame(this.animationId)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.staticMapDebounce !== null) window.clearTimeout(this.staticMapDebounce)
    this.controls.dispose()
    this.clearDataGroup()
    this.scene.remove(this.cubeWireframe)
    this.scene.remove(this.bottomPlane)
    this.cubeWireframe.geometry.dispose()
    ;(this.cubeWireframe.material as THREE.Material).dispose()
    this.bottomPlane.geometry.dispose()
    this.bottomMaterial.dispose()
    if (this.bottomMaterial.map) this.bottomMaterial.map.dispose()
    this.renderer.dispose()
    if (this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement)
    }
  }

  // ── internals ──

  private isSourceEnabled(source: number, filters: SourceFilters): boolean {
    if (source === SOURCE_LIVE_UPDATES) return filters[SOURCE_LIVE_UPDATES]
    if (source === SOURCE_FALLBACK_LOCATION) return filters[SOURCE_FALLBACK_LOCATION]
    if (source === SOURCE_SLC) return filters[SOURCE_SLC]
    return true
  }

  private clearDataGroup() {
    while (this.dataGroup.children.length > 0) {
      const child = this.dataGroup.children[0]!
      this.dataGroup.remove(child)
      if ((child as THREE.Mesh).geometry) {
        ;(child as THREE.Mesh).geometry.dispose()
      }
      const m = (child as THREE.Mesh).material
      if (m) {
        if (Array.isArray(m)) m.forEach((mm) => mm.dispose())
        else (m as THREE.Material).dispose()
      }
    }
  }

  private addVisits(visits: VisitRecord[], windowStart: number, windowEnd: number, bounds: LatLngBounds) {
    const span = windowEnd - windowStart
    // Use InstancedMesh for performance: one geometry, N transforms.
    const geometry = new THREE.CylinderGeometry(1, 1, 1, 24)
    // Cylinder geometry is centered on origin with axis on Y; translate the
    // anchor to the BOTTOM so we can place its base at arrival altitude.
    geometry.translate(0, 0.5, 0)
    // Use a white base color so per-instance setColorAt() multiplies through
    // cleanly. (vertexColors: true would require a color attribute on the
    // geometry itself, which we don't want.)
    const material = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.55,
    })
    const mesh = new THREE.InstancedMesh(geometry, material, visits.length)
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

    const dummy = new THREE.Object3D()
    let written = 0
    for (const v of visits) {
      const arr = toEpoch(v.arrivalDate)
      const dep = v.departureDate ? toEpoch(v.departureDate) : windowEnd
      // Skip visits fully outside the window.
      if (dep < windowStart || arr > windowEnd) continue
      const arrClamped = Math.max(arr, windowStart)
      const depClamped = Math.min(dep, windowEnd)

      const baseY = ((arrClamped - windowStart) / span) * CUBE_HEIGHT
      const topY = ((depClamped - windowStart) / span) * CUBE_HEIGHT
      const height = Math.max(0.05, topY - baseY)
      const radius = visitRadius(v.horizontalAccuracy)

      const { u, vCoord } = uv(v.latitude, v.longitude, bounds)
      dummy.position.set(u * CUBE_SIZE, baseY, -vCoord * CUBE_SIZE)
      dummy.scale.set(radius, height, radius)
      dummy.rotation.set(0, 0, 0)
      dummy.updateMatrix()
      mesh.setMatrixAt(written, dummy.matrix)

      const color = v.departureDate ? VISIT_COLOR_COMPLETE : VISIT_COLOR_ONGOING
      mesh.setColorAt(written, color)
      written++
    }
    mesh.count = written
    mesh.instanceMatrix.needsUpdate = true
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
    if (written > 0) this.dataGroup.add(mesh)
    else {
      geometry.dispose()
      material.dispose()
    }
  }

  /**
   * 2D mode: visits as bars standing on the map plane (y=0). Bar height encodes
   * duration (NOT placement in time). Same blue/orange colour scheme as 3D.
   */
  private addVisits2D(visits: VisitRecord[], windowStart: number, windowEnd: number, bounds: LatLngBounds) {
    const geometry = new THREE.CylinderGeometry(1, 1, 1, 24)
    geometry.translate(0, 0.5, 0) // anchor at bottom face
    const material = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.7,
    })
    const mesh = new THREE.InstancedMesh(geometry, material, visits.length)
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

    const dummy = new THREE.Object3D()
    let written = 0
    for (const v of visits) {
      const arr = toEpoch(v.arrivalDate)
      const dep = v.departureDate ? toEpoch(v.departureDate) : windowEnd
      if (dep < windowStart || arr > windowEnd) continue

      // Duration is computed from the *unclamped* arrival/departure so a visit
      // that pre-dates the window still has its true height.
      const duration = Math.max(0, dep - arr)
      const height = Math.max(0.05, visitBarHeight2D(duration))
      const radius = visitRadius(v.horizontalAccuracy)

      const { u, vCoord } = uv(v.latitude, v.longitude, bounds)
      dummy.position.set(u * CUBE_SIZE, 0, -vCoord * CUBE_SIZE)
      dummy.scale.set(radius, height, radius)
      dummy.rotation.set(0, 0, 0)
      dummy.updateMatrix()
      mesh.setMatrixAt(written, dummy.matrix)

      const color = v.departureDate ? VISIT_COLOR_COMPLETE : VISIT_COLOR_ONGOING
      mesh.setColorAt(written, color)
      written++
    }
    mesh.count = written
    mesh.instanceMatrix.needsUpdate = true
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
    if (written > 0) this.dataGroup.add(mesh)
    else {
      geometry.dispose()
      material.dispose()
    }
  }

  /**
   * 2D mode: trackpoints lie flat on the map plane (y = TRACKPOINT_2D_Y),
   * connected directly in timestamp order. Dots keep the same colour-by-source
   * scheme as 3D mode.
   */
  private addTrackPoints2D(points: TrackPoint[], filters: SourceFilters, bounds: LatLngBounds) {
    const groups = new Map<number, TrackPointGroup>()
    const lineSegments: number[] = []
    let prev: { x: number; y: number; z: number } | null = null

    for (const p of points) {
      if (!this.isSourceEnabled(p.source, filters)) continue
      const { u, vCoord } = uv(p.latitude, p.longitude, bounds)
      const x = u * CUBE_SIZE
      const z = -vCoord * CUBE_SIZE
      const y = TRACKPOINT_2D_Y

      const grp = getTrackPointGroup(groups, p.source)
      grp.positions.push(x, y, z)

      if (prev) lineSegments.push(prev.x, prev.y, prev.z, x, y, z)
      prev = { x, y, z }
    }

    if (lineSegments.length > 0) {
      const lineGeo = new THREE.BufferGeometry()
      lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(lineSegments, 3))
      const lineMat = new THREE.LineBasicMaterial({
        color: 0x007aff,
        transparent: true,
        opacity: 0.9,
      })
      this.dataGroup.add(new THREE.LineSegments(lineGeo, lineMat))
    }

    for (const [source, grp] of groups) {
      if (grp.positions.length === 0) continue
      const rgb = SOURCE_RGB[source] ?? [200, 200, 200]
      const colorVec = new THREE.Color(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255)

      const ptsGeo = new THREE.BufferGeometry()
      ptsGeo.setAttribute('position', new THREE.Float32BufferAttribute(grp.positions, 3))
      const ptsMat = new THREE.PointsMaterial({
        color: colorVec,
        size: 0.14,
        sizeAttenuation: true,
      })
      this.dataGroup.add(new THREE.Points(ptsGeo, ptsMat))
    }
  }

  private addTrackPoints(
    points: TrackPoint[],
    filters: SourceFilters,
    windowStart: number,
    windowEnd: number,
    bounds: LatLngBounds,
  ) {
    const span = windowEnd - windowStart
    // Dots are binned by source for colour. Lines connect every visible point
    // directly in timestamp order, regardless of source or time gap.
    const groups = new Map<number, TrackPointGroup>()
    const lineSegments: number[] = []
    let prev: { x: number; y: number; z: number } | null = null

    for (const p of points) {
      if (!this.isSourceEnabled(p.source, filters)) continue
      const t = toEpoch(p.timestamp)
      if (t < windowStart || t > windowEnd) continue

      const { u, vCoord } = uv(p.latitude, p.longitude, bounds)
      const x = u * CUBE_SIZE
      const z = -vCoord * CUBE_SIZE
      const y = ((t - windowStart) / span) * CUBE_HEIGHT

      const grp = getTrackPointGroup(groups, p.source)
      grp.positions.push(x, y, z)

      if (prev) lineSegments.push(prev.x, prev.y, prev.z, x, y, z)
      prev = { x, y, z }
    }

    if (lineSegments.length > 0) {
      const lineGeo = new THREE.BufferGeometry()
      lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(lineSegments, 3))
      const lineMat = new THREE.LineBasicMaterial({
        color: 0x007aff,
        transparent: true,
        opacity: 0.85,
      })
      const lines = new THREE.LineSegments(lineGeo, lineMat)
      this.dataGroup.add(lines)
    }

    for (const [source, grp] of groups) {
      if (grp.positions.length === 0) continue
      const rgb = SOURCE_RGB[source] ?? [200, 200, 200]
      const colorVec = new THREE.Color(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255)

      // Points (one small sphere via Points + circular sprite-style material)
      const ptsGeo = new THREE.BufferGeometry()
      ptsGeo.setAttribute('position', new THREE.Float32BufferAttribute(grp.positions, 3))
      const ptsMat = new THREE.PointsMaterial({
        color: colorVec,
        size: 0.12,
        sizeAttenuation: true,
      })
      const pts = new THREE.Points(ptsGeo, ptsMat)
      this.dataGroup.add(pts)
    }
  }

  private refitBottomMap(points: { lat: number; lng: number }[]) {
    // Debounce: callers update on every slider tick.
    if (this.staticMapDebounce !== null) window.clearTimeout(this.staticMapDebounce)
    this.staticMapDebounce = window.setTimeout(() => {
      const fit = fitStaticMap(points, this.apiKey)
      if (!fit) return
      this.currentBounds = fit.bounds
      const loader = new THREE.TextureLoader()
      loader.crossOrigin = 'anonymous'
      loader.load(
        fit.url,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace
          tex.minFilter = THREE.LinearFilter
          tex.magFilter = THREE.LinearFilter
          // Replace the existing material's map.
          if (this.bottomMaterial.map) this.bottomMaterial.map.dispose()
          this.bottomMaterial.map = tex
          this.bottomMaterial.color.set(0xffffff)
          this.bottomMaterial.needsUpdate = true
        },
        undefined,
        (err) => {
          console.error('[TrackPoints3D] Static map texture load failed', err)
        },
      )
    }, 250)
  }

  private startLoop() {
    const animate = () => {
      this.controls.update()
      this.renderer.render(this.scene, this.camera)
      this.animationId = window.requestAnimationFrame(animate)
    }
    animate()
  }

  private handleResize() {
    const w = this.container.clientWidth
    const h = this.container.clientHeight
    if (w === 0 || h === 0) return
    // updateStyle=true so the canvas's CSS width/height match the container,
    // not its (DPR-scaled) drawing buffer.
    this.renderer.setSize(w, h, true)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
  }
}

// Re-exports so callers can use the same map helpers for debugging if needed.
export { buildStaticMapUrl, imageBoundsAt }
